import type {
  Brand,
  BrandHomeConfig,
  RegistrationField,
  RegistrationForm,
  RegistrationSubmission,
} from "../../shared/contracts";
export function tokyoWeekStart(now: string = new Date().toISOString()) {
  const day = new Date(new Date(now).getTime() + 9 * 3600000)
    .toISOString()
    .slice(0, 10);
  const value = new Date(day + "T00:00:00Z");
  value.setUTCDate(value.getUTCDate() - ((value.getUTCDay() + 6) % 7));
  return value.toISOString().slice(0, 10);
}
export function shiftWeek(day: string, delta: number) {
  return new Date(Date.parse(day + "T00:00:00Z") + delta * 7 * 86400000)
    .toISOString()
    .slice(0, 10);
}
/** Read the same query on stable-slug and legacy-ID brand routes. */
export function readBrandCalendarLocation(
  search: string,
  now = new Date().toISOString(),
) {
  const params = new URLSearchParams(search);
  const validDate = (value: string | null): value is string => {
    if (!value || !/^[1-9]\d{3}-\d{2}-\d{2}$/.test(value)) return false;
    const date = new Date(value + "T00:00:00Z");
    return (
      Number.isFinite(date.getTime()) &&
      date.toISOString().slice(0, 10) === value
    );
  };
  const suppliedWeek = params.get("week");
  const week = validDate(suppliedWeek)
    ? tokyoWeekStart(suppliedWeek + "T00:00:00+09:00")
    : tokyoWeekStart(now);
  const suppliedDay = params.get("day");
  const day =
    validDate(suppliedDay) &&
    suppliedDay >= week &&
    suppliedDay < shiftWeek(week, 1)
      ? suppliedDay
      : "";
  return { week, day };
}
export function brandCalendarSharePath(
  brandId: string,
  week: string,
  day = "",
) {
  const params = new URLSearchParams({ week });
  if (day) params.set("day", day);
  return `/brands/${encodeURIComponent(brandId)}?${params}#brand-calendar`;
}
export function themeText(color: string) {
  const channels = [1, 3, 5]
    .map((i) => parseInt(color.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  const luminance =
    channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  return luminance > 0.3
    ? "#15251d"
    : luminance > 0.179
      ? "#000000"
      : "#ffffff";
}
export function defaultHome(b: Brand): BrandHomeConfig {
  return (
    b.home ?? {
      logo: b.image,
      cover: b.image,
      themeColor: "#e51f3d",
      tagline: b.description,
      about: b.description,
      contact: "",
      layout: "activity",
      defaultView: "week",
      sections: ["featured", "activities", "about", "contact"],
      featuredEventIds: [],
    }
  );
}
export type SignupDraft = {
  participantName: string;
  answers: Record<string, string | string[]>;
  choices: string[];
};
type StorageLike = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem" | "length" | "key"
>;
const prefix = "juyici:signup:";
const key = (event: string, user: string | null) =>
  prefix + encodeURIComponent(event) + ":" + (user ?? "anonymous");
export function saveSignupDraft(
  s: StorageLike,
  event: string,
  user: string | null,
  draft: SignupDraft,
) {
  s.setItem(key(event, user), JSON.stringify(draft));
}
export function intendSignupLogin(s: StorageLike, event: string) {
  s.setItem(prefix + "login", event);
}
export function completeSignupLogin(s: StorageLike, user: string) {
  const event = s.getItem(prefix + "login");
  if (!event) return;
  const anonymous = s.getItem(key(event, null));
  s.removeItem(prefix + "login");
  s.removeItem(key(event, null));
  if (anonymous) s.setItem(key(event, user), anonymous);
}
export function restoreSignupDraft(
  s: StorageLike,
  event: string,
  user: string | null,
): SignupDraft | null {
  if (user && s.getItem(prefix + "login") === event)
    completeSignupLogin(s, user);
  try {
    const value = JSON.parse(s.getItem(key(event, user)) ?? "null");
    return value &&
      typeof value.participantName === "string" &&
      value.answers &&
      Array.isArray(value.choices)
      ? value
      : null;
  } catch {
    return null;
  }
}
export function removeSignupDraft(
  s: StorageLike,
  event: string,
  user: string | null,
) {
  s.removeItem(key(event, user));
}
export function clearSignupDrafts(s: StorageLike) {
  for (let i = s.length - 1; i >= 0; i--) {
    const k = s.key(i);
    if (k?.startsWith(prefix)) s.removeItem(k);
  }
}
export function participantQuery(filters: {
  q: string;
  eventId: string;
  status: string;
}) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters))
    if (value) query.set(key, value);
  return query.toString();
}

export function registrationPayload(
  form: RegistrationForm | undefined,
  input: RegistrationSubmission,
): RegistrationSubmission {
  const answers: RegistrationSubmission["answers"] = {};
  for (const field of form?.fields ?? []) {
    const answer = input.answers[field.id];
    if (answer === undefined) continue;
    if (field.type === "multi")
      answers[field.id] = Array.isArray(answer)
        ? answer.filter((v) => field.options.includes(v))
        : [];
    else
      answers[field.id] =
        typeof answer === "string"
          ? field.type === "single" && !field.options.includes(answer)
            ? ""
            : answer
          : "";
  }
  return {
    formVersion: form?.version ?? 0,
    participantName: input.participantName,
    answers,
  };
}

export interface DefaultFormState {
  brandId: string;
  requestId: number;
  status: "idle" | "loading" | "error" | "ready";
  fields: RegistrationField[];
  error: string;
}
type DefaultFormAction =
  | { type: "load"; brandId: string; requestId: number }
  | {
      type: "success";
      brandId: string;
      requestId: number;
      fields: DefaultFormState["fields"];
    }
  | { type: "failure"; brandId: string; requestId: number; error: string }
  | { type: "edit"; brandId: string; fields: DefaultFormState["fields"] }
  | { type: "nameOnly"; brandId: string };
export const initialDefaultForm: DefaultFormState = {
  brandId: "",
  requestId: 0,
  status: "idle",
  fields: [],
  error: "",
};
/** Only a current successful load or deliberate name-only choice enables edits/save. */
export function defaultFormReducer(
  state: DefaultFormState,
  action: DefaultFormAction,
): DefaultFormState {
  if (action.type === "load")
    return {
      brandId: action.brandId,
      requestId: action.requestId,
      status: "loading",
      fields: [],
      error: "",
    };
  if (action.type === "nameOnly")
    return {
      ...state,
      brandId: action.brandId,
      status: "ready",
      fields: [],
      error: "",
    };
  if (action.brandId !== state.brandId) return state;
  if (action.type === "edit")
    return state.status === "ready"
      ? { ...state, fields: action.fields }
      : state;
  if (state.status !== "loading" || action.requestId !== state.requestId)
    return state;
  return action.type === "success"
    ? { ...state, status: "ready", fields: action.fields, error: "" }
    : { ...state, status: "error", error: action.error };
}
export function readyDefaultFields(
  state: DefaultFormState,
  brandId: string,
): DefaultFormState["fields"] | null {
  return !!brandId && state.brandId === brandId && state.status === "ready"
    ? state.fields
    : null;
}
